/**
 * 全项目唯一取色源 + 线性空间颜色运算(方案 B1)。
 * 所有颜色以 0xRRGGBB number 存储;插值/变暗/变亮一律在 **线性光空间** 完成,
 * 避免渐变途中发灰(方案 B2「颜色插值实现」)。
 */

export const P = {
  ember: 0xffb347,   // 烛火主色(烛民、灯窗、香火)
  emberHi: 0xffd98a, // 烛火高光
  emberLo: 0xc97a2b, // 烛火暗部
  moon: 0xcfe0ff,    // 月/星/冷光
  divine: 0xffe9c4,  // 神迹金光(日出、神像、闪白底色)
  aurora1: 0x5ef2c8, // 极光青
  aurora2: 0x9d7bff, // 极光紫
  rain: 0xa8c8e8,    // 雨丝
  snow: 0xeef4ff,    // 雪
  danger: 0x2a2233,  // 乌云/遗忘之雾
} as const;

// ── 基础拆合 ──
export function rgb(c: number): [number, number, number] {
  return [(c >> 16) & 0xff, (c >> 8) & 0xff, c & 0xff];
}
export function fromRgb(r: number, g: number, b: number): number {
  const cl = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return (cl(r) << 16) | (cl(g) << 8) | cl(b);
}

// ── sRGB <-> 线性 ──
const _lin = (v: number) => Math.pow(v / 255, 2.2);
const _srgb = (v: number) => Math.pow(Math.max(0, v), 1 / 2.2) * 255;

/** 线性空间插值两色 */
export function mix(a: number, b: number, t: number): number {
  const [ar, ag, ab] = rgb(a);
  const [br, bg, bb] = rgb(b);
  const l = (x: number, y: number) => _srgb(_lin(x) + (_lin(y) - _lin(x)) * t);
  return fromRgb(l(ar, br), l(ag, bg), l(ab, bb));
}
export const lerpColor = mix;

export const darken = (c: number, amount: number) => mix(c, 0x000000, amount);
export const lighten = (c: number, amount: number) => mix(c, 0xffffff, amount);

// ── HSL(天气修饰:饱和度/明度/色相偏移)──
export function rgbToHsl(c: number): [number, number, number] {
  let [r, g, b] = rgb(c);
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return [h, s, l];
}
export function hslToRgb(h: number, s: number, l: number): number {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let [r, g, b] = [0, 0, 0];
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return fromRgb((r + m) * 255, (g + m) * 255, (b + m) * 255);
}

/**
 * 提饱和:按 mul 提升,上限 cap,但**绝不降低**原饱和度。
 * 用于派生剪影/rim 色——低饱和的天光底色(长暮)需要提饱和才能 darken 出有色相的剪影,
 * 而本就高饱和的黎明/黄昏底色必须原样保留(直接用 satClamp 会把它们削灰)。
 */
export function saturateUp(c: number, mul: number, cap: number): number {
  const [h, s, l] = rgbToHsl(c);
  return hslToRgb(h, Math.max(s, Math.min(s * mul, cap)), l);
}

/** 综合调整:色相偏移(度)/饱和度乘/明度乘/饱和度上限 */
export function adjust(
  c: number,
  opts: { hueShift?: number; satMul?: number; satClamp?: number; valMul?: number; valAdd?: number },
): number {
  let [h, s, l] = rgbToHsl(c);
  if (opts.hueShift) h += opts.hueShift;
  if (opts.satMul != null) s *= opts.satMul;
  if (opts.satClamp != null) s = Math.min(s, opts.satClamp);
  if (opts.valMul != null) l *= opts.valMul;
  if (opts.valAdd != null) l += opts.valAdd;
  return hslToRgb(h, Math.max(0, Math.min(1, s)), Math.max(0, Math.min(1, l)));
}

// ── CSS 字符串(给 HUD / 调试)──
export function toHexStr(c: number): string {
  return '#' + c.toString(16).padStart(6, '0');
}
